import React from 'react';
import { WarningFilled, WarningAltFilled } from '@core/icons-react';
import type { InputWrapperProps } from '../types/input.types';

export function InputWrapper({
  children,
  className = '',
  disabled,
  enableCounter,
  helperText,
  hideLabel,
  id,
  inline,
  invalid,
  invalidText,
  labelText,
  description,
  rightAction,
  maxCount,
  readOnly,
  required,
  size = 'md',
  textCount = 0,
  warn,
  warnText,
  hideErrorIcon = false,
}: InputWrapperProps) {
  const prefix = 'core';
  
  const inputWrapperClasses = [
    `${prefix}-text-input-wrapper`,
    className,
    inline && `${prefix}-text-input-wrapper--inline`,
    readOnly && `${prefix}-text-input-wrapper--readonly`,
    inline && invalid && `${prefix}-text-input-wrapper--inline--invalid`,
    inline && warn && `${prefix}-text-input-wrapper--inline--warning`,
  ].filter(Boolean).join(' ');

  const labelClasses = [
    `${prefix}-label`,
    hideLabel && `${prefix}-visually-hidden`,
    disabled && `${prefix}-label--disabled`,
    inline && `${prefix}-label--inline`,
    inline && size && `${prefix}-label--inline--${size}`,
  ].filter(Boolean).join(' ');

  const helperTextClasses = [
    `${prefix}-form__helper-text`,
    disabled && `${prefix}-form__helper-text--disabled`,
    inline && `${prefix}-form__helper-text--inline`,
  ].filter(Boolean).join(' ');

  const fieldOuterWrapperClasses = [
    `${prefix}-text-input__field-outer-wrapper`,
    inline && `${prefix}-text-input__field-outer-wrapper--inline`,
  ].filter(Boolean).join(' ');

  const counterClasses = [
    `${prefix}-label`,
    `${prefix}-text-input__label-counter`,
    disabled && `${prefix}-label--disabled`,
  ].filter(Boolean).join(' ');

  const normalizedInvalid = invalid && !readOnly && !disabled;
  const normalizedWarn = warn && !invalid && !readOnly && !disabled;

  const label = labelText && !description ? (
    <label htmlFor={id} className={labelClasses}>
      {labelText}
      {required && <span className={`${prefix}-label__required`}>*</span>}
    </label>
  ) : null;

  const descriptionElement = description ? (
    <div className={`${prefix}-text-input__description`}>
      {description}
    </div>
  ) : null;

  const counter = enableCounter && maxCount ? (
    <div className={counterClasses}>
      {`${textCount}/${maxCount}`}
    </div>
  ) : null;

  const rightActionElement = rightAction ? (
    <div className={`${prefix}-text-input__right-action`}>
      {rightAction}
    </div>
  ) : null;

  const labelWrapper = label || descriptionElement || counter || rightActionElement ? (
    <div className={`${prefix}-text-input__label-wrapper`}>
      {label || descriptionElement}
      {counter || rightActionElement}
    </div>
  ) : null;

  const helper = helperText ? (
    <div id={`${id}-helper-text`} className={helperTextClasses}>
      {helperText}
    </div>
  ) : null;

  const errorMessage = normalizedInvalid && invalidText ? (
    <div id={`${id}-error-msg`} className={`${prefix}-form-requirement`}>
      {invalidText}
    </div>
  ) : null;

  const warningMessage = normalizedWarn && warnText ? (
    <div id={`${id}-warn-msg`} className={`${prefix}-form-requirement ${prefix}-form-requirement--warning`}>
      {warnText}
    </div>
  ) : null;

  const fieldWrapper = (
    <div className={`${prefix}-text-input__field-wrapper`} data-invalid={normalizedInvalid || null} data-warn={normalizedWarn || null}>
      {children}
      {normalizedInvalid && !hideErrorIcon && (
        <WarningFilled 
          className={`${prefix}-text-input__invalid-icon`}
          size={16}
        />
      )}
      {normalizedWarn && !hideErrorIcon && (
        <WarningAltFilled 
          className={`${prefix}-text-input__invalid-icon ${prefix}-text-input__invalid-icon--warning`}
          size={16}
        />
      )}
    </div>
  );

  if (inline) {
    return (
      <div className={inputWrapperClasses}>
        <div className={fieldOuterWrapperClasses}>
          {labelWrapper}
          {fieldWrapper}
        </div>
        {!normalizedInvalid && !normalizedWarn && helper}
        {errorMessage}
        {warningMessage}
      </div>
    );
  }

  return (
    <div className={inputWrapperClasses}>
      {labelWrapper}
      <div className={fieldOuterWrapperClasses}>
        {fieldWrapper}
        {!normalizedInvalid && !normalizedWarn && helper}
      </div>
      {errorMessage}
      {warningMessage}
    </div>
  );
}
